import { hasConsent } from './consentManager';

const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID;

/**
 * Load Facebook Pixel script dynamically
 */
const loadFacebookPixelScript = (): void => {
    if (document.querySelector('script[src*="facebook.net/en_US/fbevents.js"]')) {
        console.log('Facebook Pixel script already loaded');
        return;
    }

    // Initialize fbq function
    const f = window;
    const b = document;
    const e = 'script';

    if (!f.fbq) {
        const n: any = f.fbq = function() {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];

        const t = b.createElement(e) as HTMLScriptElement;
        t.async = true;
        t.src = 'https://connect.facebook.net/en_US/fbevents.js';

        const s = b.getElementsByTagName(e)[0];
        s.parentNode?.insertBefore(t, s);
    }
};

/**
 * Initialize Facebook Pixel
 * Only called when user has given marketing consent
 */
export const initializeFacebookPixel = (): void => {
    // Check consent before initializing
    if (!hasConsent('marketing')) {
        console.log('Facebook Pixel initialization skipped - no consent');
        return;
    }

    if (!META_PIXEL_ID) {
        console.warn('Facebook Pixel ID not found in environment variables');
        return;
    }

    try {
        // Load script first
        loadFacebookPixelScript();

        // Wait a moment for script to be available
        setTimeout(() => {
            if (typeof window.fbq !== 'undefined') {
                // Initialize the pixel with the ID from environment variables
                window.fbq('init', META_PIXEL_ID);

                // Grant consent
                window.fbq('consent', 'grant');

                // Track initial page view
                window.fbq('track', 'PageView');

                console.log('Facebook Pixel initialized with ID:', META_PIXEL_ID);
            }
        }, 100);
    } catch (error) {
        console.error('Failed to initialize Facebook Pixel:', error);
    }
};

/**
 * Revoke Facebook Pixel consent
 * Called when user withdraws marketing consent
 */
export const revokeFacebookPixelConsent = (): void => {
    try {
        if (typeof window.fbq !== 'undefined') {
            window.fbq('consent', 'revoke');
            console.log('Facebook Pixel consent revoked');
        }
    } catch (error) {
        console.error('Error revoking Facebook Pixel consent:', error);
    }
};

/**
 * Helper function to track Facebook Pixel events
 * Checks consent before tracking
 */
export const trackFacebookPixelEvent = (eventName: string, parameters?: any): void => {
    if (!hasConsent('marketing')) {
        console.log('Facebook Pixel event tracking skipped - no consent');
        return;
    }

    if (typeof window.fbq !== 'undefined' && META_PIXEL_ID) {
        window.fbq('track', eventName, parameters);
    }
};

/**
 * Common Facebook Pixel events with consent checks
 */
export const facebookPixelEvents = {
    track: (eventName: string, parameters?: any) => trackFacebookPixelEvent(eventName, parameters),
    viewContent: (parameters?: any) => trackFacebookPixelEvent('ViewContent', parameters),
    lead: (parameters?: any) => trackFacebookPixelEvent('Lead', parameters),
    completeRegistration: (parameters?: any) => trackFacebookPixelEvent('CompleteRegistration', parameters),
    initiateCheckout: (parameters?: any) => trackFacebookPixelEvent('InitiateCheckout', parameters),
    purchase: (parameters?: any) => trackFacebookPixelEvent('Purchase', parameters),
    addPaymentInfo: (parameters?: any) => trackFacebookPixelEvent('AddPaymentInfo', parameters),
    contact: (parameters?: any) => trackFacebookPixelEvent('Contact', parameters)
};

export default {
    initialize: initializeFacebookPixel,
    revokeConsent: revokeFacebookPixelConsent,
    track: trackFacebookPixelEvent,
    events: facebookPixelEvents
}; 